import { joiResolver } from "@hookform/resolvers/joi";
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from "react-hook-form";
import InputMask from 'react-input-mask';
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { animateScroll as scroll } from 'react-scroll';

import logo from '../../../../constants/images/logo.svg';
import paymentMethod from "../../../../constants/images/payment-method.svg";
import background from '../../../../constants/images/registration-step-1-icon.svg';
import { linkStyles } from "../../../../constants/linkStyles";
import { MAIN_ROUTES } from "../../../../routing/main_routes";
import { registrationCleanerActions } from "../../../../store/slices/registrationCleanerSlice";
import { registrationCleanerStep1Validator } from "../../../../validators/registrationCleanerStep1.validator";
import styles from './RegistrationPageCleanerStep1.module.scss';

const RegistrationPageCleanerStep1 = ({ setCurrentStep }) => {
  const dispatch = useDispatch();
  const { user_data, taxpayer_number, credit_cart_number } = useSelector(state => state.registrationCleaner);
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const refId = searchParams.get('ref_id');

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    setError,
  } = useForm({
    resolver: joiResolver(registrationCleanerStep1Validator),
    mode: 'all',
  });

  const creditCard = watch('credit_cart_number');
  const mobileNumber = watch('mobile_nummer');

  useEffect(() => {
    setValue('last_name', user_data.last_name);
    setValue('first_name', user_data.first_name);
    setValue('mobile_nummer', user_data.mobile_nummer);
    setValue('password', user_data.password);
    setValue('confirm_password', user_data.confirm_password);
    setValue('email', user_data.email);
    setValue('taxpayer_number', taxpayer_number);
    setValue('credit_cart_number', credit_cart_number);

    if (refId) {
      setValue("invited_by", refId);
    }
  }, [setValue, user_data, taxpayer_number, credit_cart_number, refId]);

  const onSubmit = useCallback((data) => {
    if (mobileNumber?.includes("_")) {
      setError("mobile_nummer", { message: "Неправильний номер телефону" });
      return;
    }
    if (creditCard?.includes("_")) {
      setError("credit_cart_number", { message: "Неправильний номер карти" });
      return;
    }

    const { taxpayer_number, credit_cart_number, ...user } = data;

    dispatch(registrationCleanerActions.setUserData({ ...user }));
    dispatch(registrationCleanerActions.setTaxpayerNumber(taxpayer_number));
    dispatch(registrationCleanerActions.setCreditCartNumber(credit_cart_number));

    setCurrentStep(prev => prev + 1);
  }, [dispatch, setError, mobileNumber, creditCard, setCurrentStep]);

  const onError = useCallback(() => {
    scroll.scrollToTop();
  }, []);

  return (
    <div className={styles.registrationPageCleanerStep1}>
      <div className={styles.registrationPageCleanerStep1_registration}>
        <div className={styles.registrationPageCleanerStep1_registration_logo}>
          <img src={logo} alt="logo icon" />
        </div>

        <h2 className={styles.registrationPageCleanerStep1_registration_title}>
          <span>1/3 </span>Реєстрація
        </h2>

        <form
          autoComplete='off'
          onSubmit={handleSubmit(onSubmit, onError)}
          className={styles.registrationPageCleanerStep1_registration_form}
        >
          <div className={styles.registrationPageCleanerStep1_registration_form_fullName}>
            <div className={styles.labelContainer}>
              <label className={styles.label}>
                <span>Прізвище <sup>*</sup></span>
                <input
                  id={"last_name"}
                  {...register('last_name')}
                  className={styles.input}
                  type="text"
                  placeholder={'Введіть прізвище'}
                  style={{
                    borderColor: errors.last_name && "#F04438"
                  }}
                  autoComplete="new_last_name"
                />
              </label>
              {errors.last_name &&
                <span className={styles.error}> {errors.last_name.message}</span>}
            </div>

            <div className={styles.labelContainer}>
              <label className={styles.label}>
                <span>Ім'я <sup>*</sup></span>
                <input
                  id={"first_name"}
                  {...register('first_name')}
                  className={styles.input}
                  type="text"
                  placeholder={"Введіть ім'я"}
                  style={{
                    borderColor: errors.first_name && "#F04438"
                  }}
                  autoComplete="new_first_name"
                />
              </label>
              {errors.first_name &&
                <span className={styles.error}> {errors.first_name.message}</span>}
            </div>
          </div>

          <div className={styles.labelContainer}>
            <label className={styles.label}>
              <span>Пароль <sup>*</sup></span>
              <input
                id={"password"}
                {...register('password')}
                className={styles.input}
                type="password"
                placeholder={"Створіть пароль"}
                style={{
                  borderColor: errors.password && "#F04438"
                }}
                autoComplete="new_password"
              />
            </label>
            {errors.password &&
              <span className={styles.error}> {errors.password.message}</span>}
          </div>

          <div className={styles.labelContainer}>
            <label className={styles.label}>
              <span>Підтвердіть пароль <sup>*</sup></span>
              <input
                id={"confirm_password"}
                {...register('confirm_password')}
                className={styles.input}
                type="password"
                placeholder={"Повторіть пароль"}
                style={{
                  borderColor: errors.confirm_password && "#F04438"
                }}
                autoComplete="new_confirm_password"
              />
            </label>
            {errors.confirm_password &&
              <span className={styles.error}> {errors.confirm_password.message}</span>}
          </div>

          <div className={styles.labelContainer}>
            <label className={styles.label}>
              <span>Електронна пошта <sup>*</sup></span>
              <input
                id={"email"}
                {...register('email')}
                className={styles.input}
                type="email"
                placeholder={'example@gmail.com'}
                style={{
                  borderColor: errors.email && "#F04438"
                }}
                autoComplete="new_email"
              />
            </label>
            {errors.email &&
              <span className={styles.error}> {errors.email.message}</span>}
          </div>

          <div className={styles.registrationPageCleanerStep1_registration_form_details}>
            <div className={styles.labelContainer}>
              <label className={styles.label}>
                <span>Ідентифікаційний код <sup>*</sup></span>
                <input
                  id={"taxpayer_number"}
                  {...register('taxpayer_number')}
                  className={styles.input}
                  type="text"
                  placeholder={"Введіть ваш ІПН"}
                  style={{
                    borderColor: errors.taxpayer_number && "#F04438"
                  }}
                  autoComplete="new_taxpayer_number"
                />
              </label>
              {errors.taxpayer_number &&
                <span className={styles.error}> {errors.taxpayer_number.message}</span>}
            </div>

            <div className={styles.labelContainer}>
              <label className={styles.label}>
                <span>Мобільний телефон <sup>*</sup></span>
                <InputMask
                  className={styles.input}
                  id={"mobile_nummer"}
                  {...register('mobile_nummer')}
                  mask="+38 (099) 999-99-99"
                  placeholder="+38 (0XX) XXX-XX-XX"
                  value={mobileNumber || ""}
                  style={{
                    borderColor: errors.mobile_nummer && "#F04438"
                  }}
                  autoComplete="new_mobile_nummer"
                />
              </label>
              {(errors.mobile_nummer &&
                  <span className={styles.error}> {errors.mobile_nummer.message}</span>)}
            </div>
          </div>

          <div className={styles.registrationPageCleanerStep1_registration_form_card}>
            <img src={paymentMethod} alt="payment method icon" />
            <div className={styles.labelContainer}>
              <label className={styles.label}>
                Банківська карта
                <InputMask
                  id={"credit_cart_number"}
                  className={`${styles.input} ${styles.card} ${errors.credit_cart_number ? styles.error : ""}`}
                  {...register('credit_cart_number')}
                  mask="9999 9999 9999 9999"
                  value={creditCard || ""}
                  placeholder={"Номер вашої карти"}
                  autoComplete="new_credit_cart_number"
                />
              </label>
              {(errors.credit_cart_number &&
                  <span className={styles.error}> {errors.credit_cart_number.message}</span>)}
            </div>
          </div>

          <div className={styles.labelContainer}>
            <label className={styles.label}>
              <span>Реферальний ID</span>
              <input
                {...register('invited_by')}
                className={styles.input}
                type="text"
                placeholder={"Реферальний ID"}
                disabled={!!refId}
                autoComplete="new_invited_by"
              />
            </label>
            {(errors.mobile_nummer &&
              <span className={styles.error}> {errors.mobile_nummer.message}</span>)}
          </div>

          <div className={styles.registrationPageCleanerStep1_registration_form_options}>
            <p>Вже зареєстровані? <Link style={linkStyles} to={MAIN_ROUTES.LOGIN}>Увійти</Link></p>

            <button
              type={'submit'}
              className={styles.button}
            >Продовжити
            </button>
          </div>
        </form>
      </div>

      <div className={styles.registrationPageCleanerStep1_icon}>
        <img src={background} alt="cleaning process icon" />
      </div>
    </div>
  );
};

export default RegistrationPageCleanerStep1;
